/**
 * Unix-cron helpers for Cloud Scheduler (five fields: minute hour dom month dow).
 * Client-side courtesy only — GCP still validates on write.
 */

export type CronScheduleIssue =
  | { kind: "empty" }
  | { kind: "field-count"; count: number }
  | { kind: "field"; field: CronFieldName; reason: "range" | "syntax" };

export type CronFieldName = "minute" | "hour" | "dayOfMonth" | "month" | "dayOfWeek";

export type CronScheduleCheck =
  | { ok: true; expression: string; summaryKey: CronSummaryKey; summaryParams?: Record<string, string> }
  | { ok: false; issue: CronScheduleIssue };

/** Stable keys the UI maps to i18n — keep logic free of sentences. */
export type CronSummaryKey =
  | "everyMinute"
  | "everyNMinutes"
  | "hourly"
  | "everyNHours"
  | "dailyAt"
  | "weekdaysAt"
  | "weeklyAt"
  | "generic";

export interface CronTemplate {
  id: string;
  expression: string;
  /** i18n default English — UI wraps with t(). */
  label: string;
}

const FIELD_SPECS: ReadonlyArray<{ name: CronFieldName; min: number; max: number }> = [
  { name: "minute", min: 0, max: 59 },
  { name: "hour", min: 0, max: 23 },
  { name: "dayOfMonth", min: 1, max: 31 },
  { name: "month", min: 1, max: 12 },
  // GCP accepts 0–6 or 7 for Sunday.
  { name: "dayOfWeek", min: 0, max: 7 },
];

/** Ops-facing presets — short labels, not a cron encyclopedia. */
export const SCHEDULED_JOB_CRON_TEMPLATES: readonly CronTemplate[] = [
  { id: "hourly", expression: "0 * * * *", label: "Every hour" },
  { id: "every15m", expression: "*/15 * * * *", label: "Every 15 min" },
  { id: "every6h", expression: "0 */6 * * *", label: "Every 6 hours" },
  { id: "dailyMidnight", expression: "0 0 * * *", label: "Daily midnight" },
  { id: "dailyMorning", expression: "0 9 * * *", label: "Daily 09:00" },
  { id: "weekdaysMorning", expression: "0 9 * * 1-5", label: "Weekdays 09:00" },
  { id: "weeklySunday", expression: "0 0 * * 0", label: "Sundays midnight" },
];

export function checkCronSchedule(raw: string): CronScheduleCheck {
  const expression = raw.trim().replace(/\s+/g, " ");
  if (!expression) return { ok: false, issue: { kind: "empty" } };

  const fields = expression.split(" ");
  if (fields.length !== 5) {
    return { ok: false, issue: { kind: "field-count", count: fields.length } };
  }

  for (let i = 0; i < FIELD_SPECS.length; i++) {
    const spec = FIELD_SPECS[i]!;
    const field = fields[i]!;
    const parsed = parseCronField(field, spec.min, spec.max);
    if (!parsed.ok) {
      return { ok: false, issue: { kind: "field", field: spec.name, reason: parsed.reason } };
    }
  }

  return { ok: true, expression, ...summarizeCron(fields) };
}

function parseCronField(
  field: string,
  min: number,
  max: number,
): { ok: true } | { ok: false; reason: "range" | "syntax" } {
  if (!field) return { ok: false, reason: "syntax" };
  const parts = field.split(",");
  for (const part of parts) {
    const result = parseCronPart(part, min, max);
    if (!result.ok) return result;
  }
  return { ok: true };
}

function parseCronPart(
  part: string,
  min: number,
  max: number,
): { ok: true } | { ok: false; reason: "range" | "syntax" } {
  if (part === "*") return { ok: true };

  const stepMatch = /^(\*|\d+(?:-\d+)?)\/(\d+)$/.exec(part);
  if (stepMatch) {
    const base = stepMatch[1]!;
    const step = Number(stepMatch[2]);
    if (!Number.isInteger(step) || step < 1) return { ok: false, reason: "syntax" };
    if (base === "*") return { ok: true };
    return parseRangeOrNumber(base, min, max);
  }

  return parseRangeOrNumber(part, min, max);
}

function parseRangeOrNumber(
  part: string,
  min: number,
  max: number,
): { ok: true } | { ok: false; reason: "range" | "syntax" } {
  const range = /^(\d+)-(\d+)$/.exec(part);
  if (range) {
    const start = Number(range[1]);
    const end = Number(range[2]);
    if (!Number.isInteger(start) || !Number.isInteger(end)) return { ok: false, reason: "syntax" };
    if (start < min || end > max || start > end) return { ok: false, reason: "range" };
    return { ok: true };
  }

  if (!/^\d+$/.test(part)) return { ok: false, reason: "syntax" };
  const value = Number(part);
  if (!Number.isInteger(value) || value < min || value > max) return { ok: false, reason: "range" };
  return { ok: true };
}

function summarizeCron(
  fields: string[],
): { summaryKey: CronSummaryKey; summaryParams?: Record<string, string> } {
  const [minute, hour, dom, month, dow] = fields as [string, string, string, string, string];

  if (minute === "*" && hour === "*" && dom === "*" && month === "*" && dow === "*") {
    return { summaryKey: "everyMinute" };
  }

  const everyNMinutes = /^\*\/(\d+)$/.exec(minute);
  if (
    everyNMinutes &&
    hour === "*" &&
    dom === "*" &&
    month === "*" &&
    dow === "*"
  ) {
    return { summaryKey: "everyNMinutes", summaryParams: { n: everyNMinutes[1]! } };
  }

  if (
    minute !== "*" &&
    !minute.includes("/") &&
    !minute.includes("-") &&
    !minute.includes(",") &&
    hour === "*" &&
    dom === "*" &&
    month === "*" &&
    dow === "*"
  ) {
    return { summaryKey: "hourly", summaryParams: { minute: pad2(minute) } };
  }

  const everyNHours = /^\*\/(\d+)$/.exec(hour);
  if (
    minute !== "*" &&
    !minute.includes("/") &&
    everyNHours &&
    dom === "*" &&
    month === "*" &&
    dow === "*"
  ) {
    return {
      summaryKey: "everyNHours",
      summaryParams: { n: everyNHours[1]!, minute: pad2(minute) },
    };
  }

  if (
    isSingleNumber(minute) &&
    isSingleNumber(hour) &&
    dom === "*" &&
    month === "*" &&
    dow === "*"
  ) {
    return {
      summaryKey: "dailyAt",
      summaryParams: { time: `${pad2(hour)}:${pad2(minute)}` },
    };
  }

  if (
    isSingleNumber(minute) &&
    isSingleNumber(hour) &&
    dom === "*" &&
    month === "*" &&
    (dow === "1-5" || dow === "1,2,3,4,5")
  ) {
    return {
      summaryKey: "weekdaysAt",
      summaryParams: { time: `${pad2(hour)}:${pad2(minute)}` },
    };
  }

  if (
    isSingleNumber(minute) &&
    isSingleNumber(hour) &&
    dom === "*" &&
    month === "*" &&
    isSingleNumber(dow)
  ) {
    return {
      summaryKey: "weeklyAt",
      summaryParams: {
        time: `${pad2(hour)}:${pad2(minute)}`,
        day: dow,
      },
    };
  }

  return { summaryKey: "generic", summaryParams: { expression: fields.join(" ") } };
}

function isSingleNumber(value: string): boolean {
  return /^\d+$/.test(value);
}

function pad2(value: string): string {
  return value.padStart(2, "0");
}
